import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import type {
  OrgComplianceItem,
  CreateOrgComplianceItemRequest,
  UpdateOrgComplianceItemRequest,
  CreateOrgComplianceItemCommentRequest,
  CreateOrgComplianceItemCommentResponse,
} from '../../../_types/awb';

const basePath = (orgId: string, accreditationBodyId: string, versionId: string, standardId: string) =>
  `${baseApiUrl()}/awb/accreditation-body/v1/organizations/${orgId}/accreditation-bodies/${accreditationBodyId}/versions/${versionId}/standards/${standardId}/compliance-items`;

export async function getComplianceItemsOrg(
  orgId: string,
  accreditationBodyId: string,
  versionId: string,
  standardId: string
): Promise<OrgComplianceItem[]> {
  const data = await fetcher({ method: 'get', url: basePath(orgId, accreditationBodyId, versionId, standardId) });
  return Array.isArray(data) ? data : [];
}

export async function createComplianceItemOrg(
  orgId: string,
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  body: CreateOrgComplianceItemRequest
): Promise<OrgComplianceItem> {
  return fetcher({ method: 'post', url: basePath(orgId, accreditationBodyId, versionId, standardId), body });
}

export async function updateComplianceItemOrg(
  orgId: string,
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  complianceItemId: string,
  body: UpdateOrgComplianceItemRequest
): Promise<OrgComplianceItem> {
  return fetcher({
    method: 'patch',
    url: `${basePath(orgId, accreditationBodyId, versionId, standardId)}/${complianceItemId}`,
    body,
  });
}

export async function addComplianceItemCommentOrg(
  orgId: string,
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  complianceItemId: string,
  body: CreateOrgComplianceItemCommentRequest
): Promise<CreateOrgComplianceItemCommentResponse> {
  return fetcher({
    method: 'post',
    url: `${basePath(orgId, accreditationBodyId, versionId, standardId)}/${complianceItemId}/comments`,
    body,
  });
}

export interface UseComplianceItemsOrgReturn {
  data: OrgComplianceItem[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  createItem: (vars: CreateOrgComplianceItemRequest) => void;
  createItemAsync: (vars: CreateOrgComplianceItemRequest) => Promise<OrgComplianceItem>;
  isCreatingItem: boolean;
  updateItem: (vars: { complianceItemId: string; updates: UpdateOrgComplianceItemRequest }) => void;
  updateItemAsync: (vars: { complianceItemId: string; updates: UpdateOrgComplianceItemRequest }) => Promise<OrgComplianceItem>;
  isUpdatingItem: boolean;
  addComment: (vars: { complianceItemId: string; comment: string }) => void;
  addCommentAsync: (vars: { complianceItemId: string; comment: string }) => Promise<CreateOrgComplianceItemCommentResponse>;
  isAddingComment: boolean;
}

export default function useComplianceItemsOrg(
  accreditationBodyId: string,
  versionId: string,
  standardId: string,
  options = {},
  onError?: (errors: ServerErrorResponse) => void
): UseComplianceItemsOrgReturn {
  const { currentOrganizationId, isFeatureEnabled, hasPermission } = useContext(AppContext);
  const orgId = currentOrganizationId;
  const queryClient = useQueryClient();

  const isEnabled =
    !!orgId &&
    (hasPermission('awbPremium', orgId) || isFeatureEnabled('awbPremiumDemo')) &&
    !!accreditationBodyId &&
    !!versionId &&
    !!standardId;

  const queryKey = ['compliance_items_org', orgId, accreditationBodyId, versionId, standardId] as const;

  const query = useQuery<OrgComplianceItem[], Error>(
    queryKey,
    () => getComplianceItemsOrg(orgId!, accreditationBodyId, versionId, standardId),
    {
      enabled: isEnabled,
      staleTime: 60000,
      ...options,
    }
  );

  const invalidate = () => queryClient.invalidateQueries(queryKey);

  const createMutation = useMutation<OrgComplianceItem, ServerErrorResponse, CreateOrgComplianceItemRequest>(
    (body) => createComplianceItemOrg(orgId!, accreditationBodyId, versionId, standardId, body),
    { onSuccess: invalidate, onError }
  );

  const updateMutation = useMutation<
    OrgComplianceItem,
    ServerErrorResponse,
    { complianceItemId: string; updates: UpdateOrgComplianceItemRequest }
  >(
    ({ complianceItemId, updates }) =>
      updateComplianceItemOrg(orgId!, accreditationBodyId, versionId, standardId, complianceItemId, updates),
    { onSuccess: invalidate, onError }
  );

  const addCommentMutation = useMutation<
    CreateOrgComplianceItemCommentResponse,
    ServerErrorResponse,
    { complianceItemId: string; comment: string }
  >(
    ({ complianceItemId, comment }) =>
      addComplianceItemCommentOrg(orgId!, accreditationBodyId, versionId, standardId, complianceItemId, { comment }),
    { onSuccess: invalidate, onError }
  );

  const noop = () => {};
  const reject = () => Promise.reject(new Error('Feature disabled'));

  if (!isEnabled) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve({}),
      createItem: noop,
      createItemAsync: reject as UseComplianceItemsOrgReturn['createItemAsync'],
      isCreatingItem: false,
      updateItem: noop,
      updateItemAsync: reject as UseComplianceItemsOrgReturn['updateItemAsync'],
      isUpdatingItem: false,
      addComment: noop,
      addCommentAsync: reject as UseComplianceItemsOrgReturn['addCommentAsync'],
      isAddingComment: false,
    };
  }

  return {
    ...query,
    data: query.data ?? [],
    createItem: createMutation.mutate,
    createItemAsync: createMutation.mutateAsync,
    isCreatingItem: createMutation.isLoading,
    updateItem: updateMutation.mutate,
    updateItemAsync: updateMutation.mutateAsync,
    isUpdatingItem: updateMutation.isLoading,
    addComment: addCommentMutation.mutate,
    addCommentAsync: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isLoading,
  };
}
